import React, { useState } from 'react';
import { ScarabIcon } from '../constants';
import { SiteContent } from '../content';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const { signup, quote, columns } = SiteContent.footer;
  const [email, setEmail] = useState('');
  const [signupState, setSignupState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const renderLink = (item: { label: string; url: string; external?: boolean }) => {
    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-bold opacity-70 hover:opacity-100 transition-opacity uppercase tracking-widest block py-1"
        >
          {item.label}
        </a>
      );
    }
    return (
      <Link
        key={item.label}
        to={item.url}
        className="text-xs font-bold opacity-70 hover:opacity-100 transition-opacity uppercase tracking-widest block py-1"
      >
        {item.label}
      </Link>
    );
  };

  return (
    <footer className="bg-rich-black text-[#F5F0E6] pt-24 md:pt-40 pb-12 overflow-hidden relative">
      <div className="absolute -left-20 -bottom-20 opacity-[0.03] pointer-events-none">
        <ScarabIcon className="w-[800px] h-[800px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 mb-40">
          <div className="lg:col-span-6">
            <h3 className="hero-title !text-4xl md:!text-6xl mb-10 leading-none">{signup.title}</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!email.trim() || signupState === 'submitting') return;
                setSignupState('submitting');
                try {
                  const res = await fetch('https://api.web3forms.com/submit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      access_key: 'da004216-b87f-4731-8e9e-3e683cdc367e',
                      subject: 'ERB Newsletter Signup',
                      email,
                      message: `New newsletter subscriber: ${email}`,
                    }),
                  });
                  const result = await res.json();
                  if (!result.success) throw new Error();
                  setSignupState('success');
                  setEmail('');
                } catch {
                  setSignupState('error');
                }
              }}
              className="flex w-full max-w-lg border-b-2 border-[#A65D3F] pb-4"
            >
              {signupState === 'success' ? (
                <p className="text-sm tracking-widest uppercase text-[#A65D3F]">You're in. Welcome to the movement.</p>
              ) : (
                <>
                  <input
                    type="email"
                    placeholder={signup.placeholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-transparent border-none outline-none flex-grow text-sm tracking-widest uppercase placeholder:text-white/40 text-[#F5F0E6]"
                    required
                  />
                  <button
                    type="submit"
                    disabled={signupState === 'submitting'}
                    className="text-xs tracking-widest uppercase font-black text-[#A65D3F] hover:text-white transition-colors ml-4 disabled:opacity-50"
                  >
                    {signupState === 'submitting' ? '...' : signupState === 'error' ? 'RETRY' : signup.button}
                  </button>
                </>
              )}
            </form>
          </div>

          <div className="lg:col-span-6 grid grid-cols-3 gap-8">
            <div className="flex flex-col space-y-6">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-black opacity-30">01</span>
                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-[#A65D3F]">Movement</h4>
              </div>
              {columns.movement.map(renderLink)}
            </div>
            <div className="flex flex-col space-y-6">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-black opacity-30">02</span>
                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-[#A65D3F]">Shop</h4>
              </div>
              {columns.shop.map(renderLink)}
            </div>
            <div className="flex flex-col space-y-6">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-black opacity-30">03</span>
                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-[#A65D3F]">Connect</h4>
              </div>
              {columns.connect.map(renderLink)}
            </div>
          </div>
        </div>

        <div className="text-center mb-24 max-w-3xl mx-auto">
          <div
            role="img"
            aria-label="ERB logo"
            className="w-36 mb-8 mx-auto"
            style={{
              aspectRatio: '271 / 128',
              background: 'linear-gradient(135deg, #A65D3F, #C9A227, #F5F0E6, #C9A227, #A65D3F)',
              backgroundSize: '300% 300%',
              animation: 'gradient-shift 6s ease infinite',
              WebkitMaskImage: 'url(/images/erb.png)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskImage: 'url(/images/erb.png)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
            }}
          />
          <p className="serif italic text-[#C9A227] text-2xl md:text-3xl leading-snug">
            "{quote}"
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center pt-12 border-t border-white/10 text-xs tracking-[0.5em] uppercase opacity-50">
          <div className="flex items-center space-x-4 mb-6 md:mb-0">
            <ScarabIcon className="w-6 h-6" />
            <p>EARTH RIGHTS BUSINESS — MMXXV</p>
          </div>
          <div className="flex space-x-12">
            <a href="#" className="hover:text-white transition-colors">Terms of Alignment</a>
            <a href="#" className="hover:text-white transition-colors">The Ledger</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;